import z from 'zod';
import { auditableSchema, entitySchema, PaginatedResponseBuilder, paginationParamsSchema } from '../shared.types';

export const baselinesSearchSchema = z.object({
    ...paginationParamsSchema.shape,
    subscriptionId: z.number().int(),
    isActive: z.boolean().nullable(),
    name: z.string(),
    description: z.string(),
});

export const baselineJobTitleSchema = z.object({
    id: z.number().int(),
    name: z.string(),
});

export const baselineUserSchema = z.object({
    id: z.number().int(),
    name: z.string(),
});

export const baselineSchema = z.object({
    ...entitySchema.shape,
    subscriptionId: z.number().int(),
    name: z.string().min(1, 'Name cannot be empty'),
    description: z.string().optional().nullable(),
    projectKey: z.string().optional().nullable(),
    isActive: z.boolean().nullable(),
    userIds: z.array(z.number().int()).optional().default([]),
    jobTitles: z.array(baselineJobTitleSchema).optional().default([]),
    users: z.array(baselineUserSchema).optional().default([]),
    latestTaskDate: z.string().optional(),
    createdDate: z.string().optional(),
});

export const baselinePaginatedSchema = z.object({
    ...entitySchema.shape,
    name: z.string().min(1, 'Name cannot be empty'),
    description: z.string().optional().nullable(),
    isActive: z.boolean().nullable(),
});

export const paginatedBaselineResponseSchema = PaginatedResponseBuilder(baselinePaginatedSchema);

export const baselineFiltersRequestSchema = z.object({
    subscriptionId: z.number().int(),
});

export const baselineFiltersResponseSchema = z.object({
    jobTitles: z.array(baselineJobTitleSchema),
    users: z.array(baselineUserSchema),
    usersInModeratorRole: z.array(baselineUserSchema),
});

export const baselineListItemSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    isActive: z.boolean().nullable(),
});

export const baselineListResponseSchema = z.object({
    baselines: z.array(baselineListItemSchema),
});

export const pushdownLocationSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    isProject: z.boolean().optional().nullable(),
});

export const pushdownLocationsResponseSchema = z.object({
    locations: z.array(pushdownLocationSchema),
    isProjectFeatureEnabled: z.boolean(),
});

export const pushdownRequestSchema = z.object({
    baselineId: z.number().int(),
    locationIds: z.array(z.number().int()),
});

export const previewPushdownTaskSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    daysFromOpening: z.number().int().optional().nullable(),
    dueDate: z.string().optional().nullable(),
});

export const previewPushdownResponseSchema = z.object({
    tasks: z.array(previewPushdownTaskSchema),
});

export const copyBaselineRequestSchema = z.object({
    baselineId: z.number().int(),
    name: z.string().min(1, 'Name cannot be empty'),
    description: z.string().optional().nullable(),
});

export const copyBaselineResponseSchema = z.object({
    baselineId: z.number().int(),
});

export const importBaselineRequestSchema = z.object({
    baselineId: z.number().int(),
    file: z.instanceof(File),
});

export type BaselinesSearch = z.infer<typeof baselinesSearchSchema>;
export type Baseline = z.infer<typeof baselineSchema>;
export type BaselineListItem = z.infer<typeof baselineListItemSchema>;
export type BaselineJobTitle = z.infer<typeof baselineJobTitleSchema>;
export type BaselineUser = z.infer<typeof baselineUserSchema>;
export type PaginatedBaselineResponse = z.infer<typeof paginatedBaselineResponseSchema>;
export type BaselineFiltersRequest = z.infer<typeof baselineFiltersRequestSchema>;
export type BaselineFiltersResponse = z.infer<typeof baselineFiltersResponseSchema>;
export type BaselineListResponse = z.infer<typeof baselineListResponseSchema>;
export type PushdownLocation = z.infer<typeof pushdownLocationSchema>;
export type PushdownLocationsResponse = z.infer<typeof pushdownLocationsResponseSchema>;
export type PushdownRequest = z.infer<typeof pushdownRequestSchema>;
export type PreviewPushdownTask = z.infer<typeof previewPushdownTaskSchema>;
export type PreviewPushdownResponse = z.infer<typeof previewPushdownResponseSchema>;
export type CopyBaselineRequest = z.infer<typeof copyBaselineRequestSchema>;
export type CopyBaselineResponse = z.infer<typeof copyBaselineResponseSchema>;
export type ImportBaselineRequest = z.infer<typeof importBaselineRequestSchema>;
